// The runtime view of the «Betrieb» tabs and the one way to change it: a save
// answers with the whole view, which shows until the poll catches up, and
// empties the reason it logged; a refusal keeps it for the next try, and a
// stale version reloads the view.

import { useEffect, useState } from "preact/hooks";

import { api } from "@/api/client";
import type { RuntimeView } from "@/api/types";
import { type PollResult, usePoll } from "@/hooks/usePoll";
import { t } from "@/i18n";
import { formatInt } from "@/lib/format";
import { errorDetail, reasonValid, runtimeErrorMessage } from "@/lib/runtimeChange";

/** Runs one change; true when it was saved. */
export type Save = (change: () => Promise<RuntimeView>) => Promise<boolean>;

// A saved change says so for a moment; a refusal stays until it is closed or
// the next change starts.
const SAVED_SHOWN_MS = 8_000;

export interface Notice {
  ok: boolean;
  text: string;
}

export interface RuntimeChangeState {
  poll: PollResult<RuntimeView>;
  /** The newer of the last save and the last poll. */
  data: RuntimeView | undefined;
  reason: string;
  setReason: (reason: string) => void;
  busy: boolean;
  notice: Notice | null;
  dismiss: () => void;
  /** A reason is given and no change is running. */
  canSave: boolean;
  save: Save;
}

export function useRuntimeChange(): RuntimeChangeState {
  const poll = usePoll((s) => api.runtime(s), [], { intervalMs: 60_000 });
  const [saved, setSaved] = useState<RuntimeView | undefined>();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);

  const data = saved && (!poll.data || saved.version > poll.data.version) ? saved : poll.data;

  useEffect(() => {
    if (!notice?.ok) return;
    const timer = window.setTimeout(() => setNotice(null), SAVED_SHOWN_MS);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const save: Save = async (change) => {
    setBusy(true);
    setNotice(null);
    try {
      const view = await change();
      setSaved(view);
      // The reason is in the log now; the next change needs its own.
      setReason("");
      setNotice({ ok: true, text: t("ops.saved", { version: formatInt(view.version) }) });
      return true;
    } catch (err) {
      setNotice({ ok: false, text: runtimeErrorMessage(err) });
      if (errorDetail(err).error === "version_conflict") poll.refetch();
      return false;
    } finally {
      setBusy(false);
    }
  };

  return {
    poll,
    data,
    reason,
    setReason,
    busy,
    notice,
    dismiss: () => setNotice(null),
    canSave: reasonValid(reason) && !busy,
    save,
  };
}
